import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

export interface SearchableSelectOption {
  value: string | number;
  label: string;
}

interface SearchableSelectProps {
  /** Valgt verdi (matches mot option.value via String-sammenligning). */
  value: string | number | null | undefined;
  options: SearchableSelectOption[];
  /** Kalles med valgt verdi (eller null ved tømming) + selve opsjonen. */
  onChange: (value: string | number | null, option?: SearchableSelectOption) => void;
  /** Vises når ingenting er valgt (lukket tilstand). */
  placeholder?: string;
  /** Vises i søkefeltet (åpen tilstand). */
  searchPlaceholder?: string;
  /** Tillat å tømme valget via en egen rad øverst. */
  allowClear?: boolean;
  /** Tekst for tøm-raden (når allowClear). */
  clearText?: string;
  /** Tekst når søket ikke gir treff. */
  noResultsText?: string;
  disabled?: boolean;
  /** Styling for input/knapp — send samme klasser som de øvrige select-feltene. */
  className?: string;
}

/**
 * SearchableSelect — søkbar nedtrekksliste for in-memory-lister (f.eks. leveringsmåter).
 * Brukeren kan skrive for å filtrere på label (delstreng, ufølsom for store/små bokstaver),
 * f.eks. «fra» → «Fraktbrev …». Tastatur: pil opp/ned, Enter velger, Esc lukker.
 * Presentasjonell — all tekst kommer som props slik at i18n håndteres av forelder.
 */
export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  options,
  onChange,
  placeholder = 'Velg...',
  searchPlaceholder = 'Søk...',
  allowClear = true,
  clearText = '—',
  noResultsText = 'Ingen treff',
  disabled = false,
  className = ''
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(
    () => options.find(o => String(o.value) === String(value ?? '')) || null,
    [options, value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, query]);

  // Lukk ved klikk utenfor
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const openDropdown = () => {
    if (disabled || open) return;
    setOpen(true);
    setQuery('');
    setHighlight(0);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const choose = (opt: SearchableSelectOption | null) => {
    onChange(opt ? opt.value : null, opt ?? undefined);
    setOpen(false);
    setQuery('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[highlight]) choose(filtered[highlight]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setQuery('');
    }
  };

  const displayValue = open ? query : (selected?.label ?? '');

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          readOnly={!open}
          value={displayValue}
          placeholder={open ? (selected?.label || searchPlaceholder) : placeholder}
          onClick={openDropdown}
          onFocus={openDropdown}
          onChange={(e) => { setQuery(e.target.value); setHighlight(0); }}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          className={`${className} ${open ? '' : 'cursor-pointer'} pr-9`}
        />
        <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {allowClear && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(null)}
              className={`block w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-blue-50 ${!selected ? 'bg-blue-50' : ''}`}
            >
              {clearText}
            </button>
          )}
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400">{noResultsText}</div>
          ) : (
            filtered.map((opt, i) => {
              const isSelected = String(opt.value) === String(value ?? '');
              return (
                <button
                  key={String(opt.value)}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => choose(opt)}
                  className={`block w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${i === highlight ? 'bg-blue-50' : ''} ${isSelected ? 'font-medium text-blue-700' : 'text-gray-700'}`}
                >
                  {opt.label}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
